import type { ReactNode } from "react";
import { Navigate } from "react-router";

import { useInfosecFeatureEnabled } from "~/hooks";

import { useSession } from "../feature/use-session";
import { type AdminGuard, firstAllowedAdminPath, guardPasses } from "../model/admin-registry";
import { administersOwnTenant } from "../model/session";

type Props = { guards: AdminGuard[]; children: ReactNode; redirectTo?: string; fallback?: ReactNode };

/** Renders its children once every guard passes; the fallback while the session loads; a redirect otherwise. */
export function RequireSession({ guards, children, redirectTo = "/", fallback = null }: Props) {
  const session = useSession();
  const infosecEnabled = useInfosecFeatureEnabled();
  if (session.isLoading) return <>{fallback}</>;
  const allowed = guards.every((guard) => guardPasses(guard, session, infosecEnabled));
  if (allowed) return <>{children}</>;
  // A refused administrator lands on a page they can open rather than out of the admin area.
  const target = administersOwnTenant(session)
    ? (firstAllowedAdminPath(session, infosecEnabled) ?? redirectTo)
    : redirectTo;
  return <Navigate to={target} replace />;
}
